/**
 * ZITELOU - User Details Page
 * Page de détail d'un utilisateur
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PauseCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  TruckIcon,
  CheckCircleIcon,
  InboxArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Badge, Loader, Table, ConfirmModal } from '../components/Common';
import { useApi } from '../hooks';
import api from '../services/api';
import { formatDate, formatPhone } from '../utils';
import { toast } from 'react-toastify';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, execute } = useApi();
  const [user, setUser] = useState(null);
  const [stripeDetails, setStripeDetails] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showResendOtpModal, setShowResendOtpModal] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  
  // État pour la gestion des expéditions
  const [shipmentData, setShipmentData] = useState(null);
  const [shipmentForm, setShipmentForm] = useState({ trackingUrl: '', message: '' });
  const [isUpdatingShipment, setIsUpdatingShipment] = useState(false);

  const fetchUser = useCallback(async () => {
    await execute(async () => {
      const [result, stripeData, shipment] = await Promise.all([
        api.getUserById(id),
        api.getUserStripeDetails(id),
        api.getShipmentStatus(id)
      ]);
      setUser(result);
      setStripeDetails(stripeData);
      if (shipment?.shipment) {
        setShipmentData(shipment.shipment);
        setShipmentForm({
          trackingUrl: shipment.shipment.trackingUrl || '',
          message: shipment.shipment.message || ''
        });
      }
    });
  }, [id, execute]);

  // Handler pour marquer comme expédié
  const handleMarkAsShipped = async () => {
    setIsUpdatingShipment(true);
    try {
      const result = await api.updateShipmentStatus(id, {
        status: 'shipped',
        trackingUrl: shipmentForm.trackingUrl || null,
        message: shipmentForm.message || null
      });
      setShipmentData(result.shipment);
      toast.success('Colis marqué comme expédié !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  // Handler pour marquer comme livré
  const handleMarkAsDelivered = async () => {
    setIsUpdatingShipment(true);
    try {
      const result = await api.updateShipmentStatus(id, { status: 'delivered' });
      setShipmentData(result.shipment);
      toast.success('Colis marqué comme livré !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  // Handler pour marquer téléphone Presque DIY comme reçu (déclenche automation Brevo)
  const handleMarkPhoneReceived = async () => {
    setIsUpdatingShipment(true);
    try {
      const result = await api.markPhoneReceived(id);
      setShipmentData(result.shipment);
      toast.success('Téléphone marqué comme reçu - Email envoyé au client');
    } catch (error) {
      toast.error(error.message || 'Erreur lors du marquage');
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  // Handler pour sauvegarder les infos de suivi
  const handleSaveTrackingInfo = async () => {
    setIsUpdatingShipment(true);
    try {
      const result = await api.updateShipmentStatus(id, {
        trackingUrl: shipmentForm.trackingUrl || null,
        message: shipmentForm.message || null
      });
      setShipmentData(result.shipment);
      toast.success('Informations de suivi mises à jour !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleDelete = async () => {
    try {
      await api.deleteUser(id);
      toast.success('Utilisateur supprimé avec succès');
      navigate('/users');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSuspend = async () => {
    try {
      await api.suspendUser(id);
      toast.success('Utilisateur suspendu');
      setShowSuspendModal(false);
      fetchUser();
    } catch (error) {
      toast.error('Erreur lors de la suspension');
    }
  };

  const handleResendOtp = async () => {
    setIsResendingOtp(true);
    try {
      const result = await api.resendUserOtp(id);
      if (result?.otpEmailSent) {
        toast.success('Email avec mot de passe temporaire renvoyé.');
      } else {
        toast.info('Mot de passe temporaire régénéré, mais l\'email n\'a pas pu être envoyé.');
      }
      setShowResendOtpModal(false);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'envoi du mot de passe temporaire');
    } finally {
      setIsResendingOtp(false);
    }
  };

  if (loading || !user) {
    return <Loader fullScreen text="Chargement des détails..." />;
  }

  const subscription = user.subscription;

  // Logs table columns
  const logsColumns = [
    {
      key: 'date',
      label: 'Date',
      render: (value, row) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{row.time}</p>
        </div>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (value) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'ip',
      label: 'Adresse IP',
      render: (value) => <span className="text-sm text-gray-500">{value}</span>,
    },
    {
      key: 'device',
      label: 'Appareil',
      render: (value) => (
        <Badge variant={value === 'Mobile' ? 'info' : 'default'} size="sm">
          {value}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<ArrowLeftIcon className="w-4 h-4" />}
            onClick={() => navigate('/users')}
          >
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {user.firstname} {user.lastname}
            </h1>
            <p className="text-gray-600 mt-1">{user.email}</p>
          </div>
          <Badge status={user.subscription_status} dot />
        </div>

        <div className="flex items-center gap-2">
            <Link to={`/users/${id}/edit`}>
                <Button variant="outline" leftIcon={<PencilIcon className="w-4 h-4" />}>
                    Modifier
                </Button>
            </Link>
          <Button
            variant="outline"
            leftIcon={<EnvelopeIcon className="w-4 h-4" />}
            onClick={() => setShowResendOtpModal(true)}
          >
            Renvoyer mot de passe
          </Button>
          <Button
            variant="warning"
            leftIcon={<PauseCircleIcon className="w-4 h-4" />}
            onClick={() => setShowSuspendModal(true)}
          >
            Suspendre
          </Button>
          <Button
            variant="danger"
            leftIcon={<TrashIcon className="w-4 h-4" />}
            onClick={() => setShowDeleteModal(true)}
          >
            Supprimer
          </Button>
        </div>
      </div>

      {/* User Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <Card title="Informations du Parent" className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <EnvelopeIcon className="w-4 h-4" />
                Email
              </label>
              <p className="text-gray-900">{user.email}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <CalendarIcon className="w-4 h-4" />
                Date d'inscription
              </label>
              <p className="text-gray-900">{formatDate(user.created_at)}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <ClockIcon className="w-4 h-4" />
                Dernière connexion
              </label>
              <p className="text-gray-900">{formatDate(user.last_login)}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <UserGroupIcon className="w-4 h-4" />
                Nombre d'enfants
              </label>
              <p className="text-gray-900 font-semibold">{user.children.length}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">
                Statut du compte
              </label>
              <Badge status={user.subscription_status} size="md" />
            </div>

            {/* Adresse de livraison */}
            <div className="col-span-2 border-t border-gray-200 pt-4 mt-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <MapPinIcon className="w-4 h-4" />
                Adresse de livraison
              </label>
            </div>
            
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-500 mb-1 block">Adresse</label>
              <p className="text-gray-900">{user.shippingAddress || '-'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">Code postal</label>
              <p className="text-gray-900">{user.shippingZipCode || '-'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">Ville</label>
              <p className="text-gray-900">{user.shippingCity || '-'}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
                <PhoneIcon className="w-4 h-4" />
                Téléphone
              </label>
              <p className="text-gray-900">{user.phoneNumber ? formatPhone(user.phoneNumber) : '-'}</p>
            </div>
          </div>
        </Card>

        {/* Subscription Info - utilise les données Stripe si disponibles */}
        <Card title="Abonnement">
          <div className="space-y-4">
            {/* Déterminer les valeurs depuis Stripe ou fallback sur les données locales */}
            {(() => {
              const stripeSub = stripeDetails?.subscription;
              const stripeInfo = stripeDetails?.stripeDetails;
              
              // Utiliser les données Stripe si disponibles, sinon les données locales
              const billingCycle = stripeSub?.billingCycle || subscription?.billing_cycle || 'monthly';
              const isYearly = billingCycle === 'yearly';
              const hasHardware = stripeSub?.hasHardware || subscription?.has_hardware || false;
              const hardwarePaymentType = stripeSub?.hardwarePaymentType || subscription?.hardware_payment_type;
              
              // Formule : utiliser le plan name depuis Stripe ou local
              const planName = stripeSub?.planName || subscription?.plan_name;
              
              // Détecter si c'est un abonnement manuel (pas de stripeSubscriptionId)
              const isManualSubscription = !stripeInfo?.stripeSubscriptionId;
              
              // Montant : utiliser Stripe si dispo, sinon ne pas afficher de prix pour les abonnements manuels
              const formattedAmount = stripeInfo?.totals 
                ? (isYearly ? stripeInfo.totals.formattedYearly : stripeInfo.totals.formattedMonthly)
                : null; // Ne pas afficher de prix pour les abonnements manuels

              return (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1 block">
                      Formule
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-gray-900 font-semibold">
                        {planName || '-'}
                      </p>
                      {hasHardware && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">+ Téléphone</span>}
                      {isManualSubscription && subscription?.status && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                          Créé manuellement
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1 block">
                      Cycle de facturation
                    </label>
                    <p className="text-gray-900 capitalize">
                      {isYearly ? 'Annuel' : 'Mensuel'}
                    </p>
                  </div>

                  {/* Montant - Seulement pour les abonnements Stripe */}
                  {stripeInfo?.totals ? (
                    <div>
                      <label className="text-sm font-medium text-gray-500 mb-1 block">
                        Montant
                      </label>
                      <p className="text-2xl font-bold text-gray-900">
                        {formattedAmount}
                        <span className="text-sm font-normal text-gray-500">
                          {' '}
                          / {isYearly ? 'an' : 'mois'}
                        </span>
                      </p>
                    </div>
                  ) : isManualSubscription && subscription?.status ? (
                    <div>
                      <label className="text-sm font-medium text-gray-500 mb-1 block">
                        Type d'abonnement
                      </label>
                      <p className="text-lg font-semibold text-purple-700">
                        Abonnement offert / Test
                      </p>
                      <p className="text-sm text-gray-500">
                        Pas de facturation Stripe associée
                      </p>
                    </div>
                  ) : null}

                  {/* Détail des items Stripe */}
                  {stripeInfo?.items && stripeInfo.items.length > 0 && (
                    <div className="pt-2 border-t border-gray-200">
                      <label className="text-sm font-medium text-gray-500 mb-2 block">
                        Détail de l'abonnement
                      </label>
                      <div className="space-y-1">
                        {stripeInfo.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-gray-600">{item.name}</span>
                            <span className="text-gray-900 font-medium">
                              {item.formattedAmount}/{item.interval === 'year' ? 'an' : 'mois'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section Téléphone */}
                  <div className="pt-4 border-t border-gray-200">
                    <label className="text-sm font-medium text-gray-500 mb-2 block">
                      📱 Téléphone
                    </label>
                    {hasHardware ? (
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                          Inclus dans l'abonnement
                        </span>
                        <span className="text-gray-700">
                          Paiement : <strong>
                            {hardwarePaymentType === 'cash' && (stripeSub?.promoCode ? `Comptant, prix de l’offre` : 'Comptant (169€)')}
                            {hardwarePaymentType === '12_months' && 'En 12 fois (15€/mois)'}
                            {hardwarePaymentType === '18_months' && 'En 18 fois (12€/mois)'}
                            {!hardwarePaymentType && 'Non spécifié'}
                          </strong>
                        </span>
                        {stripeSub?.promoCode && (
                          <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                            Code promo : {stripeSub.promoCode}
                          </span>
                        )}
                        {stripeSub?.referralCode && (
                          <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                            Code de parrainage utilisé : {stripeSub.referralCode}
                            {stripeSub.referralSponsor ? (
                              <span className="ml-2 font-normal">
                                (parrain : <Link to={`/users/${stripeSub.referralSponsor.id}`} className="underline">{stripeSub.referralSponsor.name || stripeSub.referralSponsor.email}</Link>)
                              </span>
                            ) : (
                              <span className="ml-2 text-red-700 font-bold">code inconnu : aucun parrain à rembourser</span>
                            )}
                            {stripeSub.referralCodeOtherUses > 0 && (
                              <span className="ml-2 text-red-700 font-bold">
                                déjà utilisé par {stripeSub.referralCodeOtherUses} autre{stripeSub.referralCodeOtherUses > 1 ? 's' : ''} commande{stripeSub.referralCodeOtherUses > 1 ? 's' : ''} : un seul remboursement
                              </span>
                            )}
                          </span>
                        )}
                        {stripeSub?.ownReferralCode && (
                          <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                            Code de parrainage du client : {stripeSub.ownReferralCode}
                            <span className="ml-2 font-normal">(n°{stripeSub.referralRank} des 100 premiers clients)</span>
                          </span>
                        )}
                        {stripeSub?.referralReward && (
                          <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                            Parrainage de {stripeSub.referralReward.refereeName || 'un filleul'} : {stripeSub.referralReward.statusLabel}
                            <Link to="/referrals" className="ml-2 underline font-normal">voir</Link>
                          </span>
                        )}
                        {stripeSub?.firstPayment && (
                          <span className="text-gray-700">
                            Premier paiement encaissé : <strong>{stripeSub.firstPayment.formatted}</strong>
                            <span className="text-xs text-gray-400 ml-1">(téléphone, 1er mois et livraison, remise déduite)</span>
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">
                        {!subscription?.status && !stripeSub
                          ? 'Aucun achat — inscription non finalisée'
                          : ['diy', 'presque_diy'].includes(subscription?.plan?.type || stripeSub?.planType)
                            ? 'Ancienne formule sans téléphone fourni'
                            : 'Téléphone : information non disponible'}
                      </span>
                    )}
                  </div>
                </>
              );
            })()}

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                Date de début
              </label>
              <p className="text-gray-900">{subscription?.start_date ? formatDate(subscription.start_date) : '-'}</p>
            </div>

            {subscription?.next_billing && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-1 block">
                  Prochaine facturation
                </label>
                <p className="text-gray-900">{formatDate(subscription.next_billing)}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                Statut
              </label>
              {subscription?.status ? (
                <Badge status={subscription.status} dot />
              ) : (
                <span className="text-gray-400">Aucun abonnement</span>
              )}
            </div>

            {subscription?.id && (
              <div className="pt-4 border-t border-gray-200">
                <Link to={`/subscriptions/${subscription.id}`}>
                  <Button variant="outline" size="sm" fullWidth>
                    Voir l'historique Stripe
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Section Expédition - Affichée pour tout utilisateur avec un abonnement */}
      {(subscription || stripeDetails?.subscription) && (
        <Card 
          title="Expédition du Téléphone"
          icon={<TruckIcon className="w-5 h-5" />}
        >
          <div className="space-y-4">
            {/* Statut actuel */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">
                  Statut actuel
                </label>
                <div className="flex items-center gap-2">
                  {shipmentData?.status === 'pending' && (
                    <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                      ⏳ En attente
                    </span>
                  )}
                  {shipmentData?.status === 'phone_received' && (
                    <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                      📱 Téléphone reçu
                    </span>
                  )}
                  {shipmentData?.status === 'shipped' && (
                    <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                      📦 Expédié
                    </span>
                  )}
                  {shipmentData?.status === 'delivered' && (
                    <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                      ✅ Livré
                    </span>
                  )}
                  {!shipmentData && (
                    <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                      En attente
                    </span>
                  )}
                </div>
                {shipmentData?.shippedAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    Expédié le {formatDate(shipmentData.shippedAt)}
                  </p>
                )}
              </div>
            </div>

            {/* Formulaire pour le lien de suivi */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lien de suivi (optionnel)
                </label>
                <input
                  type="url"
                  value={shipmentForm.trackingUrl}
                  onChange={(e) => setShipmentForm(prev => ({ ...prev, trackingUrl: e.target.value }))}
                  placeholder="https://laposte.fr/suivi/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message pour le client (optionnel)
                </label>
                <textarea
                  value={shipmentForm.message}
                  onChange={(e) => setShipmentForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Ex: Votre colis sera livré sous 2-3 jours ouvrés..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col gap-3 pt-4">
              {/* Presque DIY: Bouton téléphone reçu (uniquement quand pending) */}
              {stripeDetails?.subscription?.planType === 'presque_diy' && shipmentData?.status === 'pending' && (
                <Button
                  variant="outline"
                  onClick={handleMarkPhoneReceived}
                  disabled={isUpdatingShipment}
                  className="w-full"
                >
                  <InboxArrowDownIcon className="w-4 h-4 mr-2" />
                  {isUpdatingShipment ? 'En cours...' : '📱 Téléphone client reçu (envoie email)'}
                </Button>
              )}

              {/* Bouton expédié - Pour Presque DIY: après phone_received, pour autres: quand pending */}
              {(
                (stripeDetails?.subscription?.planType === 'presque_diy' && shipmentData?.status === 'phone_received') ||
                (stripeDetails?.subscription?.planType !== 'presque_diy' && shipmentData?.status === 'pending')
              ) && (
                <Button
                  onClick={handleMarkAsShipped}
                  disabled={isUpdatingShipment}
                  className="w-full"
                >
                  <TruckIcon className="w-4 h-4 mr-2" />
                  {isUpdatingShipment ? 'En cours...' : '📦 Téléphone expédié (envoie email)'}
                </Button>
              )}

              {shipmentData?.status === 'shipped' && (
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleSaveTrackingInfo}
                    disabled={isUpdatingShipment}
                  >
                    Mettre à jour le suivi
                  </Button>
                  <Button
                    onClick={handleMarkAsDelivered}
                    disabled={isUpdatingShipment}
                    className="flex-1"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    {isUpdatingShipment ? 'En cours...' : '✅ Marquer comme livré'}
                  </Button>
                </div>
              )}

              {shipmentData?.status === 'delivered' && (
                <p className="text-green-600 font-medium">
                  ✅ Ce colis a été livré !
                </p>
              )}
            </div>

            {/* Afficher le lien de suivi actuel s'il existe */}
            {shipmentData?.trackingUrl && (
              <div className="pt-4 border-t border-gray-200">
                <label className="text-sm font-medium text-gray-500 block mb-1">
                  Lien de suivi actuel
                </label>
                <a 
                  href={shipmentData.trackingUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline break-all"
                >
                  {shipmentData.trackingUrl}
                </a>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Children */}
      <Card
        title="Enfants Rattachés"
        subtitle={`${user.children.length} enfant(s)`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {user.children.map((child) => (
            <div
              key={child.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-primary/30 transition-colors"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-lg">
                    {child.firstname.charAt(0)}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {child.firstname} {child.lastname}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {child.age} ans
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Contacts autorisés</span>
                  <span className="text-gray-900 font-medium">
                    {child.authorizedContacts?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Applications</span>
                  <span className="text-gray-900 font-medium">
                    {child.authorizedApps?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Activity Logs */}
      <Card title="Logs d'Activité" subtitle="Historique des actions">
        <Table
          columns={logsColumns}
          data={user.logs}
          keyField="id"
          striped
          hoverable
        />
      </Card>

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer ${user.firstname} ${user.lastname} ? Cette action est irréversible et supprimera également tous les enfants rattachés.`}
        confirmText="Supprimer définitivement"
        confirmVariant="danger"
      />

      {/* Resend OTP Modal */}
      <ConfirmModal
        isOpen={showResendOtpModal}
        onClose={() => setShowResendOtpModal(false)}
        onConfirm={handleResendOtp}
        title="Renvoyer un mot de passe temporaire"
        message={`Un nouveau mot de passe temporaire sera généré et envoyé à ${user.email}. L'utilisateur devra le changer à sa prochaine connexion.`}
        confirmText="Renvoyer"
        confirmVariant="primary"
        loading={isResendingOtp}
      />

      {/* Suspend Modal */}
      <ConfirmModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        onConfirm={handleSuspend}
        title="Suspendre l'utilisateur"
        message={`Êtes-vous sûr de vouloir suspendre ${user.firstname} ${user.lastname} ? L'utilisateur ne pourra plus accéder à son compte.`}
        confirmText="Suspendre"
        confirmVariant="warning"
      />
    </div>
  );
};

export default UserDetails;
